import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

type RegisterFn = (id: string, isActive: boolean) => void;

/**
 * Internal context used by `FilterDrawerContainer` to let each filter
 * component self-report whether it currently holds a non-default value.
 * The container aggregates the reports to drive the badge count.
 *
 * Filters used outside of `FilterDrawerContainer` simply no-op — the
 * registration hook checks for `null` and skips the side effect.
 */
const FilterActivityContext = createContext<RegisterFn | null>(null);

/**
 * Called by each filter component (e.g. SearchFilter, MultiSelectFilter)
 * when a `filterId` is provided. Reports whether the filter currently
 * holds an "active" (non-default) value.
 *
 * - No-op when used outside a provider, so filters remain reusable standalone.
 * - Cleanup deliberately sets `false` on unmount so when a filter is
 *   conditionally removed from the tree it stops contributing to the count.
 */
export const useRegisterFilterActivity = (
  filterId: string | undefined,
  isActive: boolean
): void => {
  const register = useContext(FilterActivityContext);

  useEffect(() => {
    if (!register || !filterId) return undefined;
    register(filterId, isActive);
    return () => register(filterId, false);
  }, [filterId, isActive, register]);
};

interface FilterActivityProviderProps {
  /** Called whenever the aggregated active count changes. */
  onCountChange: (count: number) => void;
  children: React.ReactNode;
}

/**
 * Provides the registration context and emits the aggregated count to the
 * host (FilterDrawerContainer). Owns the per-filter activity map.
 */
export const FilterActivityProvider: React.FC<FilterActivityProviderProps> = ({
  onCountChange,
  children,
}) => {
  const [activity, setActivity] = useState<Record<string, boolean>>({});

  const register = useCallback<RegisterFn>((id, isActive) => {
    setActivity((prev) =>
      prev[id] === isActive ? prev : { ...prev, [id]: isActive }
    );
  }, []);

  const count = useMemo(
    () => Object.values(activity).filter(Boolean).length,
    [activity]
  );

  useLayoutEffect(() => {
    onCountChange(count);
  }, [count, onCountChange]);

  return (
    <FilterActivityContext.Provider value={register}>
      {children}
    </FilterActivityContext.Provider>
  );
};
